import { Plus, Trash2 } from 'lucide-react'

export interface ListOptionsTableEditorProps {
  /** Conteúdo de `list_data` (array de objetos). */
  listData: unknown[] | undefined
  /** Campo armazenado (ex.: `id`). */
  valueField: string
  /** Campo mostrado ao utilizador (ex.: `name`). */
  displayField: string
  onChange: (next: Record<string, unknown>[]) => void
  disabled?: boolean
}

/**
 * Edição em tabela de listas do tipo `[{ id, name }, ...]` sem escrever JSON manualmente.
 */
export function ListOptionsTableEditor({
  listData,
  valueField,
  displayField,
  onChange,
  disabled = false
}: ListOptionsTableEditorProps) {
  const rows: Record<string, unknown>[] = Array.isArray(listData)
    ? listData.map((row) => (row && typeof row === 'object' ? { ...(row as object) } : {}))
    : []

  const updateRow = (index: number, patch: Partial<Record<string, unknown>>) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r))
    onChange(next)
  }

  const addRow = () => {
    onChange([...rows, { [valueField]: '', [displayField]: '' }])
  }

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">{displayField || 'Texto'}</th>
              <th className="text-left px-2 py-1.5 font-medium">{valueField || 'Valor'}</th>
              <th className="w-10 px-1 py-1.5" aria-label="Remover" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-2 py-3 text-gray-500 text-center">
                  Nenhuma opção. Use &quot;Adicionar opção&quot;.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={index} className="border-t border-gray-100">
                  <td className="p-1">
                    <input
                      type="text"
                      disabled={disabled}
                      value={String(row[displayField] ?? '')}
                      onChange={(e) => updateRow(index, { [displayField]: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                      placeholder="Texto na lista"
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="text"
                      disabled={disabled}
                      value={String(row[valueField] ?? '')}
                      onChange={(e) => updateRow(index, { [valueField]: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                      placeholder="id / valor"
                    />
                  </td>
                  <td className="p-1 text-center">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => removeRow(index)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      aria-label="Remover linha"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={addRow}
        className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50"
      >
        <Plus className="w-4 h-4" />
        Adicionar opção
      </button>
    </div>
  )
}
